import React from 'react';
import { Button, Flex, Text, Switch, PillToggle } from '@chatsdet/ui';
import { MacroRichTextField } from './MacroRichTextField';
import { Plus, Trash, Warning, XCircle, Eye, PencilSimple } from '@phosphor-icons/react';
import type { AssertionSeverity, InlineAssertion } from '@chatsdet/types';
import {
  SEVERITY_OPTIONS,
  AssertionSeverityHelp,
  DEFAULT_NEW_ASSERTION_SEVERITY,
} from './AssertionSeverityControls';

type ActionAssertionsEditorProps = {
  assertions: InlineAssertion[];
  onAdd: (assertion: InlineAssertion) => void;
  onUpdate: (id: string, updates: Partial<InlineAssertion>) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
};

/** Click cycles fatal → fail → warn → fatal. */
const NEXT_SEVERITY: Record<AssertionSeverity, AssertionSeverity> = {
  fatal: 'fail',
  fail: 'warn',
  warn: 'fatal',
};

function SeverityBadge({
  severity,
  interactive,
  onToggle,
}: {
  severity: AssertionSeverity;
  interactive?: boolean;
  onToggle?: () => void;
}) {
  const classes =
    severity === 'warn'
      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      : severity === 'fatal'
        ? 'bg-red-600/15 text-red-700 dark:text-red-300'
        : 'bg-red-500/10 text-red-600 dark:text-red-400';
  const icon =
    severity === 'warn' ? <Warning size={10} weight="fill" /> : <XCircle size={10} weight="fill" />;
  const label = severity === 'warn' ? 'Warn' : severity === 'fatal' ? 'Fatal' : 'Fail';

  if (interactive && onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium cursor-pointer hover:opacity-80 transition-opacity ${classes}`}
        title={`Click to switch to ${NEXT_SEVERITY[severity]}`}
      >
        {icon}
        {label}
      </button>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${classes}`}
    >
      {icon}
      {label}
    </span>
  );
}

function AssertionRow({
  assertion,
  onUpdate,
  onDelete,
}: {
  assertion: InlineAssertion;
  onUpdate: (updates: Partial<InlineAssertion>) => void;
  onDelete: () => void;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editCondition, setEditCondition] = React.useState(assertion.condition);

  const handleSave = React.useCallback(() => {
    const trimmed = editCondition.trim();
    if (!trimmed) return;
    onUpdate({ condition: trimmed });
    setIsEditing(false);
  }, [editCondition, onUpdate]);

  const handleCancel = React.useCallback(() => {
    setEditCondition(assertion.condition);
    setIsEditing(false);
  }, [assertion.condition]);

  if (isEditing) {
    return (
      <div className="space-y-4">
        <MacroRichTextField
          multiline
          label="Condition"
          value={editCondition}
          onChange={setEditCondition}
          autoFocus
          rows={2}
        />
        <div className="space-y-1">
          <PillToggle
            value={assertion.severity}
            onChange={(v) => onUpdate({ severity: v })}
            options={SEVERITY_OPTIONS}
          />
          <AssertionSeverityHelp severity={assertion.severity} />
        </div>
        <div className="space-y-1">
          <Switch
            size="sm"
            isSelected={assertion.agentic ?? false}
            onChange={(v) => onUpdate({ agentic: v || undefined })}
            label={
              <span className="inline-flex items-center gap-1">
                <Eye size={12} />
                Requires agent evaluation
              </span>
            }
          />
          <Text size="xs" className="text-muted-foreground">
            Visual checks or complex evaluations that cannot be cached.
          </Text>
        </div>
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onPress={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={!editCondition.trim()}
            onPress={handleSave}
          >
            Save
          </Button>
        </Flex>
      </div>
    );
  }

  return (
    <div className="group rounded-md bg-muted/30 px-3 py-2 space-y-1">
      <Flex align="start" justify="between" gap="sm">
        <Text
          size="sm"
          className="flex-1 break-words cursor-pointer hover:text-foreground/80"
          onClick={() => setIsEditing(true)}
        >
          {assertion.condition}
        </Text>
        <Flex align="center" gap="xs" className="shrink-0">
          <SeverityBadge
            severity={assertion.severity}
            interactive
            onToggle={() => onUpdate({ severity: NEXT_SEVERITY[assertion.severity] })}
          />
          {assertion.agentic && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-purple-500/10 px-2 py-0.5 text-[10px] font-medium text-purple-600 dark:text-purple-400"
              title="Requires agent evaluation (cannot be cached)"
            >
              <Eye size={10} weight="fill" />
              Agent
            </span>
          )}
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Edit assertion"
            onPress={() => setIsEditing(true)}
            className="text-muted-foreground hover:text-foreground opacity-40 group-hover:opacity-100 transition-opacity"
          >
            <PencilSimple size={14} />
          </Button>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Remove assertion"
            onPress={onDelete}
            className="text-muted-foreground hover:text-destructive opacity-40 group-hover:opacity-100 transition-opacity"
          >
            <Trash size={14} />
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}

export const ActionAssertionsEditor: React.FC<ActionAssertionsEditorProps> = ({
  assertions,
  onAdd,
  onUpdate,
  onDelete,
  onClearAll,
}) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const [newCondition, setNewCondition] = React.useState('');
  const [newSeverity, setNewSeverity] = React.useState<AssertionSeverity>(
    DEFAULT_NEW_ASSERTION_SEVERITY
  );
  const [newAgentic, setNewAgentic] = React.useState(false);

  const resetAddForm = React.useCallback(() => {
    setIsAdding(false);
    setNewCondition('');
    setNewSeverity(DEFAULT_NEW_ASSERTION_SEVERITY);
    setNewAgentic(false);
  }, []);

  const handleSubmit = React.useCallback(() => {
    const trimmed = newCondition.trim();
    if (!trimmed) return;
    onAdd({
      id: crypto.randomUUID(),
      condition: trimmed,
      severity: newSeverity,
      agentic: newAgentic || undefined,
    });
    resetAddForm();
  }, [newCondition, newSeverity, newAgentic, onAdd, resetAddForm]);

  const hasAny = assertions.length > 0;

  const addForm = isAdding ? (
    <div className="mt-3 space-y-4">
      <MacroRichTextField
          multiline
        label="Condition"
        placeholder="e.g., Success message appears"
        value={newCondition}
        onChange={setNewCondition}
        autoFocus
        rows={2}
      />
      <div className="space-y-1">
        <PillToggle
          value={newSeverity}
          onChange={setNewSeverity}
          options={SEVERITY_OPTIONS}
        />
        <AssertionSeverityHelp severity={newSeverity} />
      </div>
      <div className="space-y-1">
        <Switch
          size="sm"
          isSelected={newAgentic}
          onChange={setNewAgentic}
          label={
            <span className="inline-flex items-center gap-1">
              <Eye size={12} />
              Requires agent evaluation
            </span>
          }
        />
        <Text size="xs" className="text-muted-foreground">
          Enable for visual checks or complex evaluations that cannot be cached (e.g., layout
          verification, image comparison).
        </Text>
      </div>
      <Flex gap="sm" justify="end">
        <Button variant="ghost" size="sm" onPress={resetAddForm}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          isDisabled={!newCondition.trim()}
          onPress={handleSubmit}
        >
          Add
        </Button>
      </Flex>
    </div>
  ) : (
    <div className="mt-3">
      <Button
        variant="outline"
        size="sm"
        iconLeft={<Plus size={14} />}
        onPress={() => setIsAdding(true)}
      >
        Add assertion
      </Button>
    </div>
  );

  return (
    <div>
      {hasAny && (
        <Flex align="center" justify="end" className="mb-2">
          <Button
            variant="ghost"
            size="sm"
            onPress={onClearAll}
            className="text-muted-foreground"
          >
            Clear all
          </Button>
        </Flex>
      )}
      <Text size="sm" className="text-muted-foreground">
        Conditions verified after this action completes. These also guide the agent on expected
        outcomes.
      </Text>

      {hasAny && (
        <div className="mt-3 space-y-2">
          {assertions.map((a) => (
            <AssertionRow
              key={a.id}
              assertion={a}
              onUpdate={(updates) => onUpdate(a.id, updates)}
              onDelete={() => onDelete(a.id)}
            />
          ))}
        </div>
      )}

      {addForm}
    </div>
  );
};
